/**
 * ExpiryCalendar - Certification Expiry Calendar Widget
 *
 * Calendar-based visualization of certification expirations:
 * - Monthly calendar view showing expiry dates
 * - Color-coded urgency (critical, warning, normal)
 * - Click to view/renew expiring certifications
 * - Summary statistics by time period
 *
 * @epic DC-523 Vendor Certification Program
 * @task DC-554 (Phase 12.4)
 */

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import '../../../styles/vendor.css';

// ============================================================================
// TYPES
// ============================================================================

interface ExpiringCertification {
  cert_id: string;
  cert_number: string;
  vendor_id: string;
  vendor_name: string;
  product_id: string;
  product_name: string;
  tier: 'Platinum' | 'Gold' | 'Silver' | 'Bronze';
  expires_at: string;
  days_until_expiry: number;
  ai_readiness_score: number;
}

interface CalendarDay {
  date: Date;
  isCurrentMonth: boolean;
  isToday: boolean;
  expirations: ExpiringCertification[];
}

interface MonthSummary {
  total_expiring: number;
  critical_count: number;
  warning_count: number;
  by_tier: Record<string, number>;
}

// ============================================================================
// CONSTANTS
// ============================================================================

const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

const TIER_COLORS: Record<string, string> = {
  Platinum: '#166534',
  Gold: '#ca8a04',
  Silver: '#6b7280',
  Bronze: '#b45309',
};

const URGENCY_COLORS: Record<string, { bg: string; border: string; text: string }> = {
  critical: { bg: '#fee2e2', border: '#f87171', text: '#991b1b' },
  warning: { bg: '#fef3c7', border: '#fbbf24', text: '#92400e' },
  normal: { bg: '#dbeafe', border: '#60a5fa', text: '#1e40af' },
};

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

function getUrgencyLevel(daysUntilExpiry: number): 'critical' | 'warning' | 'normal' {
  if (daysUntilExpiry <= 14) return 'critical';
  if (daysUntilExpiry <= 30) return 'warning';
  return 'normal';
}

function formatDate(date: Date): string {
  return date.toLocaleDateString('en-US', {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  });
}

function isSameDay(date1: Date, date2: Date): boolean {
  return (
    date1.getFullYear() === date2.getFullYear() &&
    date1.getMonth() === date2.getMonth() &&
    date1.getDate() === date2.getDate()
  );
}

function getCalendarDays(year: number, month: number, expirations: ExpiringCertification[]): CalendarDay[] {
  const days: CalendarDay[] = [];
  const today = new Date();

  // First day of the month
  const firstDay = new Date(year, month, 1);
  const startingDay = firstDay.getDay();

  // Last day of the month
  const lastDay = new Date(year, month + 1, 0);
  const totalDays = lastDay.getDate();

  // Previous month days
  const prevMonth = new Date(year, month, 0);
  const prevMonthDays = prevMonth.getDate();

  for (let i = startingDay - 1; i >= 0; i--) {
    const date = new Date(year, month - 1, prevMonthDays - i);
    days.push({
      date,
      isCurrentMonth: false,
      isToday: isSameDay(date, today),
      expirations: expirations.filter(e => isSameDay(new Date(e.expires_at), date)),
    });
  }

  // Current month days
  for (let i = 1; i <= totalDays; i++) {
    const date = new Date(year, month, i);
    days.push({
      date,
      isCurrentMonth: true,
      isToday: isSameDay(date, today),
      expirations: expirations.filter(e => isSameDay(new Date(e.expires_at), date)),
    });
  }

  // Next month days to fill grid
  const remainingDays = 42 - days.length; // 6 rows x 7 days
  for (let i = 1; i <= remainingDays; i++) {
    const date = new Date(year, month + 1, i);
    days.push({
      date,
      isCurrentMonth: false,
      isToday: isSameDay(date, today),
      expirations: expirations.filter(e => isSameDay(new Date(e.expires_at), date)),
    });
  }

  return days;
}

// ============================================================================
// SUB-COMPONENTS
// ============================================================================

interface CalendarHeaderProps {
  year: number;
  month: number;
  onPrevMonth: () => void;
  onNextMonth: () => void;
  onToday: () => void;
}

const CalendarHeader: React.FC<CalendarHeaderProps> = ({
  year,
  month,
  onPrevMonth,
  onNextMonth,
  onToday,
}) => (
  <div className="expiry-calendar-header">
    <div className="calendar-nav">
      <button className="btn btn-icon" onClick={onPrevMonth} title="Previous Month">
        ←
      </button>
      <h2 className="calendar-title">
        {MONTHS[month]} {year}
      </h2>
      <button className="btn btn-icon" onClick={onNextMonth} title="Next Month">
        →
      </button>
    </div>
    <button className="btn btn-secondary btn-sm" onClick={onToday}>
      Today
    </button>
  </div>
);

interface CalendarDayCellProps {
  day: CalendarDay;
  onClick: () => void;
  isSelected: boolean;
}

const CalendarDayCell: React.FC<CalendarDayCellProps> = ({ day, onClick, isSelected }) => {
  const hasExpirations = day.expirations.length > 0;
  const urgencyLevel = hasExpirations
    ? getUrgencyLevel(Math.min(...day.expirations.map(e => e.days_until_expiry)))
    : null;
  const urgencyStyle = urgencyLevel ? URGENCY_COLORS[urgencyLevel] : null;

  return (
    <div
      className={`calendar-day ${day.isCurrentMonth ? '' : 'other-month'} ${day.isToday ? 'today' : ''} ${isSelected ? 'selected' : ''} ${hasExpirations ? 'has-expirations' : ''}`}
      onClick={onClick}
      style={
        hasExpirations && urgencyStyle
          ? { backgroundColor: urgencyStyle.bg, borderColor: urgencyStyle.border }
          : undefined
      }
    >
      <span className="day-number">{day.date.getDate()}</span>
      {hasExpirations && (
        <div className="expiration-indicators">
          {day.expirations.slice(0, 3).map((exp) => (
            <div
              key={exp.cert_id}
              className="expiration-dot"
              style={{ backgroundColor: TIER_COLORS[exp.tier] }}
              title={`${exp.product_name} (${exp.tier})`}
            />
          ))}
          {day.expirations.length > 3 && (
            <span className="more-count">+{day.expirations.length - 3}</span>
          )}
        </div>
      )}
    </div>
  );
};

interface SelectedDayPanelProps {
  day: CalendarDay | null;
  onRenew: (certId: string) => void;
  onViewDetails: (certId: string) => void;
}

const SelectedDayPanel: React.FC<SelectedDayPanelProps> = ({ day, onRenew, onViewDetails }) => {
  if (!day) {
    return (
      <div className="selected-day-panel empty">
        <p>Select a day to view expiring certifications</p>
      </div>
    );
  }

  if (day.expirations.length === 0) {
    return (
      <div className="selected-day-panel empty">
        <h3>{formatDate(day.date)}</h3>
        <p>No certifications expiring on this date</p>
      </div>
    );
  }

  return (
    <div className="selected-day-panel">
      <h3>{formatDate(day.date)}</h3>
      <p className="expiry-count">{day.expirations.length} certification(s) expiring</p>
      <div className="expiration-list">
        {day.expirations.map((exp) => {
          const urgency = getUrgencyLevel(exp.days_until_expiry);
          const urgencyStyle = URGENCY_COLORS[urgency];

          return (
            <div key={exp.cert_id} className="expiration-card">
              <div className="card-header">
                <span
                  className="tier-badge"
                  style={{ color: TIER_COLORS[exp.tier] }}
                >
                  {exp.tier}
                </span>
                <span
                  className="urgency-badge"
                  style={{
                    backgroundColor: urgencyStyle.bg,
                    color: urgencyStyle.text,
                  }}
                >
                  {exp.days_until_expiry <= 0 ? 'Expired' : `${exp.days_until_expiry} days`}
                </span>
              </div>
              <div className="card-body">
                <div className="product-name">{exp.product_name}</div>
                <div className="vendor-name">{exp.vendor_name}</div>
                <div className="cert-number">#{exp.cert_number}</div>
                <div className="score">Score: {exp.ai_readiness_score}%</div>
              </div>
              <div className="card-actions">
                <button
                  className="btn btn-sm btn-secondary"
                  onClick={() => onViewDetails(exp.cert_id)}
                >
                  View
                </button>
                <button
                  className="btn btn-sm btn-primary"
                  onClick={() => onRenew(exp.cert_id)}
                >
                  Renew
                </button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

interface MonthSummaryPanelProps {
  summary: MonthSummary;
}

const MonthSummaryPanel: React.FC<MonthSummaryPanelProps> = ({ summary }) => (
  <div className="month-summary-panel">
    <h4>This Month</h4>
    <div className="summary-stats">
      <div className="stat-item">
        <span className="stat-value">{summary.total_expiring}</span>
        <span className="stat-label">Total Expiring</span>
      </div>
      <div className="stat-item critical">
        <span className="stat-value">{summary.critical_count}</span>
        <span className="stat-label">Critical (≤14d)</span>
      </div>
      <div className="stat-item warning">
        <span className="stat-value">{summary.warning_count}</span>
        <span className="stat-label">Warning (≤30d)</span>
      </div>
    </div>
    <div className="tier-breakdown">
      <h5>By Tier</h5>
      <div className="tier-items">
        {Object.entries(summary.by_tier).map(([tier, count]) => (
          <div key={tier} className="tier-item">
            <span
              className="tier-color"
              style={{ backgroundColor: TIER_COLORS[tier] }}
            />
            <span className="tier-name">{tier}</span>
            <span className="tier-count">{count}</span>
          </div>
        ))}
      </div>
    </div>
  </div>
);

interface LegendPanelProps {}

const LegendPanel: React.FC<LegendPanelProps> = () => (
  <div className="calendar-legend">
    <h5>Legend</h5>
    <div className="legend-items">
      <div className="legend-section">
        <span className="legend-title">Urgency</span>
        <div className="legend-item">
          <span
            className="legend-color"
            style={{ backgroundColor: URGENCY_COLORS.critical.bg }}
          />
          <span>Critical (≤14 days)</span>
        </div>
        <div className="legend-item">
          <span
            className="legend-color"
            style={{ backgroundColor: URGENCY_COLORS.warning.bg }}
          />
          <span>Warning (≤30 days)</span>
        </div>
        <div className="legend-item">
          <span
            className="legend-color"
            style={{ backgroundColor: URGENCY_COLORS.normal.bg }}
          />
          <span>Normal (&gt;30 days)</span>
        </div>
      </div>
      <div className="legend-section">
        <span className="legend-title">Tier</span>
        {Object.entries(TIER_COLORS).map(([tier, color]) => (
          <div key={tier} className="legend-item">
            <span className="legend-dot" style={{ backgroundColor: color }} />
            <span>{tier}</span>
          </div>
        ))}
      </div>
    </div>
  </div>
);

// ============================================================================
// MAIN COMPONENT
// ============================================================================

export const ExpiryCalendar: React.FC = () => {
  const today = new Date();
  const [currentYear, setCurrentYear] = useState(today.getFullYear());
  const [currentMonth, setCurrentMonth] = useState(today.getMonth());
  const [selectedDay, setSelectedDay] = useState<CalendarDay | null>(null);
  const [expirations, setExpirations] = useState<ExpiringCertification[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Fetch expiring certifications
  const fetchExpirations = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      // Fetch expirations for a 3-month window
      const endDate = new Date(currentYear, currentMonth + 2, 0);

      const response = await fetch(
        `/api/certifications/expiring?days=${Math.ceil((endDate.getTime() - Date.now()) / 86400000)}`
      );

      if (!response.ok) {
        throw new Error(`Failed to fetch expirations: ${response.statusText}`);
      }

      const data = await response.json();
      setExpirations(data.certifications || []);
    } catch (err) {
      console.error('Error fetching expirations:', err);
      setError(err instanceof Error ? err.message : 'Failed to load expirations');
    } finally {
      setLoading(false);
    }
  }, [currentYear, currentMonth]);

  useEffect(() => {
    fetchExpirations();
  }, [fetchExpirations]);

  // Navigation handlers
  const handlePrevMonth = () => {
    if (currentMonth === 0) {
      setCurrentYear(currentYear - 1);
      setCurrentMonth(11);
    } else {
      setCurrentMonth(currentMonth - 1);
    }
    setSelectedDay(null);
  };

  const handleNextMonth = () => {
    if (currentMonth === 11) {
      setCurrentYear(currentYear + 1);
      setCurrentMonth(0);
    } else {
      setCurrentMonth(currentMonth + 1);
    }
    setSelectedDay(null);
  };

  const handleToday = () => {
    setCurrentYear(today.getFullYear());
    setCurrentMonth(today.getMonth());
    setSelectedDay(null);
  };

  // Action handlers
  const handleRenew = (certId: string) => {
    window.location.href = `#/vendors/certifications/${certId}/renew`;
  };

  const handleViewDetails = (certId: string) => {
    window.location.href = `#/vendors/certifications/${certId}`;
  };

  // Calculate calendar days
  const calendarDays = useMemo(
    () => getCalendarDays(currentYear, currentMonth, expirations),
    [currentYear, currentMonth, expirations]
  );

  // Calculate month summary
  const monthSummary = useMemo((): MonthSummary => {
    const monthExpirations = expirations.filter((e) => {
      const expDate = new Date(e.expires_at);
      return (
        expDate.getFullYear() === currentYear && expDate.getMonth() === currentMonth
      );
    });

    const byTier: Record<string, number> = {};
    let criticalCount = 0;
    let warningCount = 0;

    for (const exp of monthExpirations) {
      byTier[exp.tier] = (byTier[exp.tier] || 0) + 1;
      const urgency = getUrgencyLevel(exp.days_until_expiry);
      if (urgency === 'critical') criticalCount++;
      else if (urgency === 'warning') warningCount++;
    }

    return {
      total_expiring: monthExpirations.length,
      critical_count: criticalCount,
      warning_count: warningCount,
      by_tier: byTier,
    };
  }, [expirations, currentYear, currentMonth]);

  if (loading) {
    return (
      <div className="expiry-calendar-page">
        <div className="calendar-loading">
          <div className="spinner" />
          <p>Loading calendar...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="expiry-calendar-page">
        <div className="calendar-error">
          <p>{error}</p>
          <button className="btn btn-secondary" onClick={fetchExpirations}>
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="expiry-calendar-page">
      <div className="calendar-container">
        <CalendarHeader
          year={currentYear}
          month={currentMonth}
          onPrevMonth={handlePrevMonth}
          onNextMonth={handleNextMonth}
          onToday={handleToday}
        />

        <div className="calendar-grid">
          <div className="calendar-weekdays">
            {WEEKDAYS.map((day) => (
              <div key={day} className="weekday-header">
                {day}
              </div>
            ))}
          </div>

          <div className="calendar-days">
            {calendarDays.map((day, idx) => (
              <CalendarDayCell
                key={idx}
                day={day}
                onClick={() => setSelectedDay(day)}
                isSelected={selectedDay ? isSameDay(day.date, selectedDay.date) : false}
              />
            ))}
          </div>
        </div>
      </div>

      <div className="calendar-sidebar">
        <MonthSummaryPanel summary={monthSummary} />
        <SelectedDayPanel
          day={selectedDay}
          onRenew={handleRenew}
          onViewDetails={handleViewDetails}
        />
        <LegendPanel />
      </div>
    </div>
  );
};

export default ExpiryCalendar;
